/**
 * The ways into the Creator, as paths.
 *
 * A leaf module on purpose: `App.tsx` needs the entry path to build the
 * redirect of an embedded start, `CreatorPage` needs it to decide its mode,
 * and `DesignEntryChoice` needs it to navigate. Holding the literal in any
 * one of them means the other two compare against a copy — which is how an
 * iframe embed pointed at the guided entry ended up in the classic editor.
 */

/** Where each entry lands in the Creator. */
export const CLASSIC_ENTRY_PATH = '/creator?new=1';
export const GUIDED_ENTRY_PATH = '/creator/guided';

/** Bulk Creation: many listings from one reference, in the Seller Area. */
export const BULK_ENTRY_PATH = '/designs/bulk';

/**
 * Strips a trailing slash and lowercases, the two ways a pathname can differ
 * from the route it matches. React Router matches `/creator/guided/` against
 * the guided route, but `serve` does not normalise the path, so an embedder
 * writing the trailing slash would otherwise be read as the classic entry.
 */
function normalizePath(pathname: string): string {
  return pathname.replace(/\/+$/, '').toLowerCase();
}

/** True when the pathname addresses the guided entry, however it is spelled. */
export function isGuidedEntryPath(pathname: string): boolean {
  return normalizePath(pathname) === GUIDED_ENTRY_PATH;
}

/**
 * `/creator/<designId>` — the route that opens an existing design. The id is
 * captured off the raw pathname, never off `normalizePath`: that lowercases,
 * and a design id is matched against the database as written.
 */
const DESIGN_PATH = /^\/creator\/([^/]+)\/*$/;

/** True when the pathname addresses Bulk Creation, however it is spelled. */
export function isBulkEntryPath(pathname: string): boolean {
  return normalizePath(pathname) === BULK_ENTRY_PATH;
}

/**
 * The entry a pathname points at; anything else is the classic one.
 *
 * Four shapes survive, and only four: the guided entry, Bulk Creation, an
 * existing design, and everything else as the classic entry. The narrowness is the point —
 * the Cloud embed is documented to start on `/creator` *or* on `/`
 * (docs/guides/CLOUD_SIGNED_EMBED.md), so a rule that simply handed the
 * pathname back would drop a Cloud seller starting on `/` into the design
 * overview instead of the Creator.
 */
export function creatorEntryPath(pathname: string): string {
  if (isGuidedEntryPath(pathname)) {
    return GUIDED_ENTRY_PATH;
  }
  if (isBulkEntryPath(pathname)) {
    return BULK_ENTRY_PATH;
  }

  const design = DESIGN_PATH.exec(pathname);
  return design ? `/creator/${design[1]}` : '/creator';
}

/** True when a creator path names an existing design rather than an entry. */
export function isDesignEntryPath(pathname: string): boolean {
  return !isGuidedEntryPath(pathname) && DESIGN_PATH.test(pathname);
}

/** True when an entry path starts a new design (and so takes `new=1`). */
export function startsNewDesign(entryPath: string): boolean {
  return !isDesignEntryPath(entryPath) && !isBulkEntryPath(entryPath);
}
